import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { formatTT, API } from "../lib/api";
import { Sparkles, TrendingUp, Lock, AlertTriangle, Star } from "lucide-react";

const KPI = ({ label, value, accent = false }) => (
    <div className={`p-6 border ${accent ? "bg-[#0B192C] text-[#F8F7F4] border-[#0B192C]" : "bg-white border-[#0B192C]/10"}`}>
        <div className={`label-overline mb-2 ${accent ? "opacity-70" : "text-[#718096]"}`}>{label}</div>
        <div className="font-serif-display text-4xl">{value}</div>
    </div>
);

const ProviderPortal = () => {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        axios.get(`${API}/portal/${token}`)
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.status === 404 ? "Invalid or revoked portal link" : "Unable to load portal"))
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center"><span className="label-overline text-[#718096]">Loading portal…</span></div>;
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8F7F4]">
                <div className="max-w-md p-8 border border-[#0B192C]/10 bg-white text-center">
                    <AlertTriangle className="w-10 h-10 mx-auto mb-4 text-[#C53030]" strokeWidth={1.25}/>
                    <h1 className="font-serif-display text-3xl mb-2">Access Denied</h1>
                    <p className="text-sm text-[#4A5568] mb-6">{error}. Please contact SmartFinanceTT to obtain a new link.</p>
                    <Link to="/" data-testid="portal-home-link" className="btn-primary px-5 py-2.5 label-overline">Back to SmartFinanceTT</Link>
                </div>
            </div>
        );
    }

    const { provider, totals, products, leads } = data;

    return (
        <div className="min-h-screen bg-[#F8F7F4]" data-testid="provider-portal">
            {/* Clean branded header (no main nav) */}
            <header className="border-b border-[#0B192C]/10 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
                    <div className="flex items-baseline gap-3">
                        <img src="/logo-smartfinancett.png" alt="SmartFinanceTT" className="h-9 w-auto" />
                        <span className="label-overline text-[#718096] hidden sm:inline">PROVIDER PORTAL</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#718096]">
                        <Lock className="w-3.5 h-3.5" strokeWidth={1.5}/>
                        <span className="font-mono-body">Secure tokenised access</span>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
                <div className="mb-10">
                    <div className="label-overline text-[#1E3A2B] mb-2">Welcome,</div>
                    <h1 className="font-serif-display text-4xl md:text-6xl tracking-tight" data-testid="portal-provider-name">{provider.name}</h1>
                    <p className="text-sm text-[#4A5568] mt-3 max-w-2xl">A live snapshot of your performance on SmartFinanceTT — impressions, qualified clicks, and leads ready for your team to action.</p>
                </div>

                {/* KPIs */}
                <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3 mb-12" data-testid="portal-kpis">
                    <KPI label="Products" value={totals.products} />
                    <KPI label="Sponsored" value={totals.featured} accent />
                    <KPI label="Impressions" value={totals.impressions.toLocaleString()} />
                    <KPI label="Apply Clicks" value={totals.clicks.toLocaleString()} />
                    <KPI label="Leads" value={totals.leads} />
                    <KPI label="CTR / Conv." value={`${totals.ctr}% / ${totals.conversion_rate}%`} />
                </div>

                {/* Products table */}
                <section className="mb-12">
                    <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="w-4 h-4 text-[#1E3A2B]" strokeWidth={1.5}/>
                        <h2 className="label-overline">Your Products</h2>
                    </div>
                    <div className="border border-[#0B192C]/10 bg-white overflow-x-auto">
                        <table className="w-full text-sm" data-testid="portal-products-table">
                            <thead className="border-b border-[#0B192C]/10">
                                <tr>
                                    <th className="text-left p-4 label-overline">Product</th>
                                    <th className="text-left p-4 label-overline">Type</th>
                                    <th className="text-right p-4 label-overline">Rate</th>
                                    <th className="text-right p-4 label-overline">Rating</th>
                                    <th className="text-right p-4 label-overline">Impressions</th>
                                    <th className="text-right p-4 label-overline">Clicks</th>
                                    <th className="text-right p-4 label-overline">Leads</th>
                                    <th className="text-right p-4 label-overline">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((p) => (
                                    <tr key={p.id} className="border-b border-[#0B192C]/5">
                                        <td className="p-4 font-serif-display text-base">{p.name}</td>
                                        <td className="p-4 font-mono-body text-xs uppercase">{p.type === "credit_card" ? "Card" : p.category}</td>
                                        <td className="p-4 text-right font-mono-body">{p.interest_rate}%</td>
                                        <td className="p-4 text-right font-mono-body">
                                            <span className="inline-flex items-center gap-1">
                                                <Star className="w-3 h-3 fill-[#D1A77E] stroke-[#D1A77E]" />
                                                {p.rating}
                                            </span>
                                        </td>
                                        <td className="p-4 text-right font-mono-body">{p.impressions.toLocaleString()}</td>
                                        <td className="p-4 text-right font-mono-body">{p.apply_clicks.toLocaleString()}</td>
                                        <td className="p-4 text-right font-mono-body">{p.leads}</td>
                                        <td className="p-4 text-right">
                                            {p.featured ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0B192C] text-[#D1A77E] text-xs">
                                                    <Sparkles className="w-3 h-3" strokeWidth={1.5}/>
                                                    {(p.sponsor_tier || "featured").toUpperCase()}
                                                </span>
                                            ) : <span className="text-xs text-[#718096]">Organic</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Leads table */}
                <section>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="label-overline">Recent Leads</h2>
                        <span className="label-overline text-[#718096]">Only your applicants</span>
                    </div>
                    <div className="border border-[#0B192C]/10 bg-white overflow-x-auto">
                        <table className="w-full text-sm" data-testid="portal-leads-table">
                            <thead className="border-b border-[#0B192C]/10">
                                <tr>
                                    <th className="text-left p-4 label-overline">Date</th>
                                    <th className="text-left p-4 label-overline">Name</th>
                                    <th className="text-left p-4 label-overline">Email</th>
                                    <th className="text-left p-4 label-overline">Phone</th>
                                    <th className="text-left p-4 label-overline">Product</th>
                                    <th className="text-left p-4 label-overline">Source</th>
                                    <th className="text-right p-4 label-overline">Income</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leads.length === 0 ? (
                                    <tr><td colSpan={7} className="p-10 text-center text-[#718096]">No leads yet. As applicants submit forms, they&rsquo;ll appear here in real time.</td></tr>
                                ) : leads.map((l) => (
                                    <tr key={l.id} className="border-b border-[#0B192C]/5">
                                        <td className="p-4 font-mono-body text-xs">{new Date(l.created_at).toLocaleString()}</td>
                                        <td className="p-4">{l.full_name}</td>
                                        <td className="p-4 font-mono-body text-xs">{l.email}</td>
                                        <td className="p-4 font-mono-body text-xs">{l.phone}</td>
                                        <td className="p-4 font-serif-display text-sm">{l.product_name}</td>
                                        <td className="p-4">
                                            {l.lead_source === "mortgage_calculator" ? (
                                                <span
                                                    title={l.mortgage_context ? `Property TT$${(l.mortgage_context.property_price || 0).toLocaleString()} · Mortgage TT$${(l.mortgage_context.mortgage_amount || 0).toLocaleString()} · ${l.mortgage_context.term_years || 0}yr @ ${(l.mortgage_context.interest_rate || 0).toFixed(2)}%` : ""}
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0A4F49] text-[#F8F7F4] text-xs cursor-help"
                                                >
                                                    <Sparkles className="w-3 h-3" strokeWidth={1.5}/>
                                                    Mortgage Calc
                                                </span>
                                            ) : (
                                                <span className="text-xs text-[#718096]">Product page</span>
                                            )}
                                        </td>
                                        <td className="p-4 text-right font-mono-body">{formatTT(l.monthly_income)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                <div className="mt-12 pt-6 border-t border-[#0B192C]/10 text-xs text-[#718096] font-mono-body">
                    SmartFinanceTT · Portal session for {provider.name} · Data refreshes on reload.
                </div>
            </div>
        </div>
    );
};

export default ProviderPortal;
